import { AutoTagTableStatus } from "@/apis/table/route";
import { AutoTagToolCallItem } from "@/components/mapping/auto-tag-progress/auto-tag-tool-call-item";
import {
  AutoTagDisplayState,
  compareTableName,
  countDisplayStates,
  foldDisplayState,
  resolveDisplayState,
  STATE_CHIP_CLASS,
  STATE_LABELS,
  toolLabel,
} from "@/components/mapping/auto-tag-progress/constants";
import { isDebugEnabled } from "@/lib/is-debug-enabled";
import {
  TAGGING_EXCLUDED_LABEL,
  TAGGING_EXCLUDED_TOOLTIP,
  taggingExcludedNotice,
} from "@/lib/tagging-target";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import {
  hasAutoTagRecord,
  isAutoTagRunning,
  useAutoTagStatus,
} from "@/hooks/mutations/use-auto-tag-status";
import { useAutoTagTables } from "@/hooks/mutations/use-auto-tag-tables";
import { useAutoTagToolCalls } from "@/hooks/mutations/use-auto-tag-tool-calls";
import { useWorkbookId } from "@/hooks/use-workbook";
import { cn } from "@/lib/utils";
import queries from "@/queries";
import { popModal } from "@/stores/modal";
import { Checkbox } from "@/styles/ui/checkbox";
import { Button } from "@/toggle-ui-kit/button";
import { ComboButton, type ComboOption } from "@/toggle-ui-kit/combo";
import { DecorateInteraction } from "@/toggle-ui-kit/decorate/interaction/decorate-interaction";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ChevronRight, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type FilterBucket =
  | "NOT_STARTED"
  | "RUNNING"
  | "SUCCESS"
  | "AXIS_FALLBACK"
  | "NEEDS_REVIEW"
  | "ALREADY_TAGGED"
  | "EMPTY"
  | "SKIPPED"
  | "FAILED"
  | "EXCLUDED";

// 필터 칩 노출 순서. PENDING/RUNNING 은 "진행 중" 한 버킷으로 합친다.
const FILTER_ORDER: FilterBucket[] = [
  "NOT_STARTED",
  "RUNNING",
  "SUCCESS",
  "AXIS_FALLBACK",
  "NEEDS_REVIEW",
  "ALREADY_TAGGED",
  "EMPTY",
  "SKIPPED",
  "FAILED",
  "EXCLUDED",
];

// STATE_LABELS 는 대부분 문장형("적절한 태그를…")이라 필터엔 짧은 별칭을 따로 쓴다.
// (검토는 행 칩에서도 짧은 "검토" 그대로 쓴다 — 일반 모드에서 EMPTY 까지 묶는 상태라서.)
const FILTER_LABELS: Record<FilterBucket, string> = {
  NOT_STARTED: "미실행",
  RUNNING: "진행 중",
  SUCCESS: "성공",
  AXIS_FALLBACK: "축대체",
  NEEDS_REVIEW: "검토",
  ALREADY_TAGGED: "태깅됨",
  EMPTY: "태그 없음",
  SKIPPED: "건너뜀",
  FAILED: "실패",
  EXCLUDED: "제외",
};

/** 표의 (접힌) 표시 상태를 필터 버킷으로 환산한다. 진행 중은 한 버킷으로 통합.
 * 일반사항·회계정책·추정 등 목차의 표는 실행 이력이 남아 있어도 "제외"다 — 규칙 도입 전 돌린 흔적이
 * 성공/실패로 섞여 보이면 왜 다시 못 돌리는지 읽히지 않는다. */
const bucketOf = (table: AutoTagTableStatus, debug: boolean): FilterBucket => {
  if (!table.taggingTarget) return "EXCLUDED";
  const state = foldDisplayState(
    resolveDisplayState(table.status, table.outcome),
    debug,
  );
  if (state === "PENDING" || state === "RUNNING") return "RUNNING";
  return state;
};

/** 체크박스를 켤 수 있는 표 — 진행 중이 아니고, 태깅 대상인 표. */
const isSelectable = (table: AutoTagTableStatus) =>
  table.taggingTarget && !isAutoTagRunning(table);

/** 모달 안의 표 한 행. 펼치면 그 표의 tool 호출 로그를 on-demand 로드한다. */
const ModalTableRow = ({
  workbookId,
  categoryId,
  table,
  selected,
  selectable,
  onToggleSelect,
}: {
  workbookId: string | undefined;
  categoryId: string | undefined;
  table: AutoTagTableStatus;
  selected: boolean;
  selectable: boolean;
  onToggleSelect: () => void;
}) => {
  const debug = isDebugEnabled();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // 이 표가 속한 category 를 알 때만 바로가기 제공. 이동 후 모달은 닫는다.
  const goToTable =
    workbookId && categoryId
      ? () => {
          navigate(
            `/mapping/${workbookId}/${categoryId}?table=${table.tableId}`,
          );
          popModal();
        }
      : undefined;
  const state = foldDisplayState(
    resolveDisplayState(table.status, table.outcome),
    debug,
  );
  const isRunning = isAutoTagRunning(table);
  // tool 호출 로그(펼치기)는 debug 전용 — 폴링도 debug 일 때만.
  const { data: toolCalls, isLoading } = useAutoTagToolCalls(
    workbookId,
    table.tableId,
    { enabled: debug && open && hasAutoTagRecord(table), isRunning },
  );

  const nameLabel = (
    <>
      {debug && (
        <ChevronRight
          className={cn(
            "size-4 shrink-0 text-label-assistive transition-transform",
            open && "rotate-90",
          )}
        />
      )}
      <span
        className="min-w-0 truncate label-2-medium text-label-normal"
        title={table.tableName}
      >
        {table.tableName}
      </span>
    </>
  );

  const trailing = (
    <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
      {isRunning && (
        <span className="max-w-[140px] truncate caption-2-regular text-blue-500">
          {toolLabel(table.currentTool)}
        </span>
      )}
      {debug && table.toolCallCount > 0 && (
        <span className="caption-2-regular text-label-assistive">
          {table.toolCallCount} 호출
        </span>
      )}
      {table.taggingTarget ? (
        <span
          className={cn(
            "flex items-center gap-1 rounded-full px-2 py-0.5 caption-2-medium",
            STATE_CHIP_CLASS[state],
          )}
        >
          {isRunning && <Loader2 className="size-3 animate-spin" />}
          {STATE_LABELS[state]}
        </span>
      ) : (
        <Tooltip label={TAGGING_EXCLUDED_TOOLTIP} side="top">
          <span
            className={cn(
              "flex items-center gap-1 rounded-full px-2 py-0.5 caption-2-medium",
              STATE_CHIP_CLASS.NOT_STARTED,
            )}
          >
            {TAGGING_EXCLUDED_LABEL}
          </span>
        </Tooltip>
      )}
    </div>
  );

  return (
    <div className="rounded-[8px] border border-line-normal bg-background-base">
      <div className="group flex w-full items-center gap-2 px-3 py-2.5">
        <Checkbox
          checked={selected}
          disabled={!selectable}
          onCheckedChange={onToggleSelect}
          aria-label={`${table.tableName} 선택`}
        />
        {debug ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex min-w-0 shrink items-center gap-2 text-left"
          >
            {nameLabel}
          </button>
        ) : (
          <div className="flex min-w-0 shrink items-center gap-2">
            {nameLabel}
          </div>
        )}
        {goToTable && (
          <Tooltip label="이 표로 이동" side="top">
            <DecorateInteraction asChild variant="normal" color="neutral">
              <button
                type="button"
                onClick={goToTable}
                aria-label={`${table.tableName} 표로 이동`}
                className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-label-alternative opacity-0 transition-opacity group-hover:opacity-100"
              >
                <ArrowUpRight className="size-4" />
              </button>
            </DecorateInteraction>
          </Tooltip>
        )}
        {trailing}
      </div>
      {debug && open && (
        <div className="flex flex-col gap-1 px-3 pb-2">
          {table.errorMessage && (
            <p className="rounded bg-red-50 p-1.5 caption-2-regular text-red-600">
              {table.errorMessage}
            </p>
          )}
          {toolCalls && toolCalls.calls.length > 0 ? (
            toolCalls.calls.map((call) => (
              <AutoTagToolCallItem key={call.seq} call={call} />
            ))
          ) : isLoading ? (
            <p className="flex items-center gap-1.5 px-1 py-1.5 caption-2-regular text-label-alternative">
              <Loader2 className="size-3 animate-spin" />
              불러오는 중…
            </p>
          ) : (
            <p className="px-1 py-1.5 caption-2-regular text-label-alternative">
              {isRunning
                ? "아직 호출이 없습니다."
                : "호출 도구 목록이 없습니다."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 워크북의 모든 taggable 표 오토태깅 진행상황 모달.
 * 미실행 표를 포함해 전체 표를 보여주고, 체크박스로 골라 실행/재시도할 수 있다.
 */
export const AutoTagProgressModal = () => {
  const debug = isDebugEnabled();
  const { data: workbookId } = useWorkbookId();
  const { data: status } = useAutoTagStatus(workbookId ?? undefined);
  const { mutate: autoTagTables, isPending: isEnqueuing } = useAutoTagTables();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<FilterBucket | "ALL">("ALL");

  // 바로가기 라우팅에 필요한 tableId → categoryId 매핑.
  // 라우트(/mapping/:categoryId)·시트 로더와 동일한 categories 쿼리의 id 를 써야
  // categoryId 가 매칭된다(tableAddresses 의 categoryId 는 id 체계가 달라 매칭 실패).
  const { data: categories } = useQuery({
    ...queries.workbook(workbookId!)._ctx.categories(),
    enabled: !!workbookId,
    select: (d) => d.categories,
  });
  const categoryIdByTable = useMemo(() => {
    const map = new Map<string, string>();
    categories?.forEach((c) => c.tables.forEach((t) => map.set(t.id, c.id)));
    return map;
  }, [categories]);

  const tables = useMemo(
    () =>
      [...(status?.tables ?? [])].sort((a, b) =>
        compareTableName(a.tableName, b.tableName),
      ),
    [status],
  );

  // 일반사항·회계정책·추정 등 목차의 표는 실행되지 않으므로 진행률·요약 수치 어디에도 끼지 않는다.
  const targetTables = tables.filter((t) => t.taggingTarget);
  const excludedCount = tables.length - targetTables.length;
  // 진행률은 시도된 표 기준 — 미실행 표가 분모에 끼면 항상 미완처럼 보인다.
  const attempted = targetTables.filter(hasAutoTagRecord);
  const done = attempted.filter((t) => !isAutoTagRunning(t)).length;
  const notStartedCount = targetTables.length - attempted.length;
  const stateCounts = countDisplayStates(targetTables, debug);
  const countState = (target: AutoTagDisplayState) =>
    stateCounts.get(target) ?? 0;
  const progress =
    attempted.length > 0 ? Math.round((done / attempted.length) * 100) : 0;

  const bucketCounts = useMemo(() => {
    const counts = new Map<FilterBucket, number>();
    tables.forEach((t) => {
      const bucket = bucketOf(t, debug);
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
    });
    return counts;
  }, [tables, debug]);
  const presentBuckets = FILTER_ORDER.filter(
    (b) => (bucketCounts.get(b) ?? 0) > 0,
  );
  // 선택한 버킷의 표가 모두 사라지면(예: 진행 중 표가 끝남) "전체"로 되돌린다.
  const activeFilter =
    filter !== "ALL" && (bucketCounts.get(filter) ?? 0) > 0 ? filter : "ALL";
  const visibleTables =
    activeFilter === "ALL"
      ? tables
      : tables.filter((t) => bucketOf(t, debug) === activeFilter);
  // 드롭다운 옵션: 전체 + 실제 존재하는 상태 버킷(개수 포함).
  const filterOptions: ComboOption[] = [
    { value: "ALL", label: `전체 ${tables.length}` },
    ...presentBuckets.map((b) => ({
      value: b,
      label: `${FILTER_LABELS[b]} ${bucketCounts.get(b) ?? 0}`,
    })),
  ];

  const selectableTables = tables.filter(isSelectable);
  const selectedRunnableIds = selectableTables
    .filter((t) => selectedIds.has(t.tableId))
    .map((t) => t.tableId);
  // "전체 선택"은 현재 필터로 보이는 표에만 작용한다(선택 자체는 필터 넘어 유지).
  const visibleSelectable = visibleTables.filter(isSelectable);
  const allVisibleSelected =
    visibleSelectable.length > 0 &&
    visibleSelectable.every((t) => selectedIds.has(t.tableId));

  const toggleSelect = (tableId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleSelectable.forEach((t) =>
        allVisibleSelected ? next.delete(t.tableId) : next.add(t.tableId),
      );
      return next;
    });
  };

  const runSelected = () => {
    autoTagTables(selectedRunnableIds, {
      onSuccess: () => setSelectedIds(new Set()),
    });
  };

  return (
    <ModalWrapper className="flex max-h-[80vh] w-[640px] max-w-[calc(100vw-48px)] flex-col gap-0 rounded-[10px] border border-line-normal bg-background-base p-0 shadow-2xl">
      <div className="flex flex-col gap-2 border-b border-line-normal px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="headline-2-medium text-label-normal">
              오토태깅 전체 진행상황
            </h2>
            <p className="mt-1 body-2-normal-regular text-label-alternative">
              {debug ? (
                <>
                  표 {done} / {attempted.length} 완료 · 성공{" "}
                  {countState("SUCCESS")} · 검토 {countState("NEEDS_REVIEW")} ·
                  빈응답 {countState("EMPTY")} · 건너뜀 {countState("SKIPPED")}{" "}
                  · 실패 {countState("FAILED")} · 축대체{" "}
                  {countState("AXIS_FALLBACK")} · 미실행 {notStartedCount}
                </>
              ) : (
                <>
                  표 {done}/{attempted.length} 처리 · 검토{" "}
                  {countState("NEEDS_REVIEW")}
                </>
              )}
            </p>
            {excludedCount > 0 && (
              <p className="mt-0.5 caption-1-regular text-label-assistive">
                {taggingExcludedNotice(excludedCount)}
              </p>
            )}
          </div>
          <button
            type="button"
            className="flex size-7 shrink-0 items-center justify-center rounded-[8px] text-label-alternative hover:bg-background-neutral-neutral"
            onClick={popModal}
            aria-label="닫기"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
          <div
            className="h-full rounded-full bg-blue-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="min-h-0 overflow-auto px-5 py-4">
        {tables.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 px-3">
              <label className="flex shrink-0 cursor-pointer items-center gap-2 caption-1-medium text-label-alternative">
                <Checkbox
                  checked={allVisibleSelected}
                  disabled={visibleSelectable.length === 0}
                  onCheckedChange={toggleSelectAll}
                  aria-label="전체 선택"
                />
                전체 선택
                {selectedRunnableIds.length > 0 && (
                  <span className="caption-2-regular text-label-assistive">
                    · {selectedRunnableIds.length}개 선택됨
                  </span>
                )}
              </label>
              {presentBuckets.length > 0 && (
                <ComboButton
                  size="28px"
                  value={activeFilter}
                  onValueChange={(v) => setFilter(v as FilterBucket | "ALL")}
                  options={filterOptions}
                  className="w-[136px] shrink-0 border border-line-normal"
                />
              )}
            </div>
            {visibleTables.map((table) => (
              <ModalTableRow
                key={table.tableId}
                workbookId={workbookId ?? undefined}
                categoryId={categoryIdByTable.get(table.tableId)}
                table={table}
                selected={selectedIds.has(table.tableId)}
                selectable={isSelectable(table)}
                onToggleSelect={() => toggleSelect(table.tableId)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-[8px] border border-line-normal bg-background-neutral-neutral p-4 caption-2-regular text-label-alternative">
            오토태깅할 수 있는 표가 없습니다.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-line-normal px-5 py-4">
        <Button
          variant="solid"
          color="primary"
          size="base"
          leftIcon={isEnqueuing && <Loader2 className="size-4 animate-spin" />}
          disabled={selectedRunnableIds.length === 0 || isEnqueuing}
          onClick={runSelected}
        >
          선택한 표 오토태깅
          {selectedRunnableIds.length > 0
            ? ` (${selectedRunnableIds.length})`
            : ""}
        </Button>
        <Button
          variant="solid"
          color="secondary"
          size="base"
          onClick={popModal}
        >
          닫기
        </Button>
      </div>
    </ModalWrapper>
  );
};
